import { QFilterBlock } from "@coreapp/CoreApp/QFilterBlock";
import { QObjDBObject } from "@coreapp/CoreApp/QObjDBObject";
import { GetSearchResults } from "@/Utils/Promises";
import { GetDoc, GetWSMgr } from "@/Utils/StateLib";
import { enumRulePairLogicCondition } from "@coreapp/CoreApp/QRulePair";
import { kEmployeeID, kNameID, kPositionID, kPositionTitleID, kTitleID } from "@coreapp/constants";
import { OverrideFactory } from "@coreapp/CoreApp/OverrideFactory";

export type sortState = { fieldName: string; order: "asc" | "desc" };

export abstract class EntityEmployeeList {
  abstract searchHolders(
    query: string,
    activeFilterBlock: QFilterBlock | null,
    sort: sortState,
    cursor?: string | undefined
  ): Promise<any[]>;
  abstract handleAssignment(
    added: QObjDBObject[],
    removed: QObjDBObject[],
    dbObj: Nullable<QObjDBObject>
  ): Promise<void>;
}

export class BaseChartEmployeeList extends EntityEmployeeList {
  async searchHolders(
    query: string,
    activeFilterBlock: QFilterBlock | null = null,
    sort: sortState,
    cursor?: string | undefined
  ) {
    const doc = GetDoc();

    const andCondition: any = enumRulePairLogicCondition.and;
    const result: any = activeFilterBlock
      ? activeFilterBlock.MarshallSeparated(andCondition, true)
      : {};

    const options: any = {
      chartID: doc?.GetIWizData().GetBaseChartID(),
      sort,
      positionFilters: result?.positionFilters || [],
      employeeFilters: result?.employeeFilters || [],
      getHoldersWithNoPosition: true
    };
    if (cursor) options["cursor"] = cursor || "";

    const results = await GetSearchResults(query, options);

    return results?.result?.results ?? [];
  }

  async handleAssignment(
    added: QObjDBObject[],
    removed: QObjDBObject[],
    dbObj: Nullable<QObjDBObject>
  ) {
    throw new Error("Should not assign/unassign in Base Charts.");
  }
}

export class CustomChartEmployeeList extends EntityEmployeeList {
  async searchHolders(query: string, activeFilterBlock: QFilterBlock | null, sort: sortState) {
    var objDB = GetDoc()?.GetObjDB();
    if (objDB == null) return [];

    var boxClass = objDB.GetClassByName("Box");
    var positionClass = objDB.GetClassByName("Position");
    var posObjectList = positionClass?.GetObjectList();
    const holdersObjects = new Set();

    if (!posObjectList) return [];

    // Search for Positions
    posObjectList.forEach((obj: QObjDBObject) => {
      const queryMatches = this.queryMatchPosition(query, obj);
      const filterMatches = activeFilterBlock
        ? activeFilterBlock.PositionsMatches(obj, GetDoc())
        : null;

      const positionMatchesFilter = activeFilterBlock ? filterMatches?.positionCondition : true;

      if (positionMatchesFilter && queryMatches) {
        obj.GetHolderList()?.forEach((holderObj: QObjDBObject) => holdersObjects.add(holderObj));
      } else {
        if (!filterMatches) return;
        obj.GetHolderList()?.forEach((holderObj: QObjDBObject) => {
          const nameMatches = ((holderObj.SmartGetFieldValue(kNameID) as string) ?? "")
            .toLowerCase()
            .includes(query.toLowerCase());
          const holderFilterMatches = activeFilterBlock
            ? filterMatches?.onlyHoldersCondition &&
              filterMatches?.holdersCondition[holderObj.GetObjectID() as string]
            : true;

          if (nameMatches && holderFilterMatches) {
            holdersObjects.add(holderObj);
          }
        });
      }
    });

    // Search for Holders
    const holdersObjectList = boxClass?.GetObjectList()?.filter((obj: QObjDBObject) => {
      const queryMatches = this.queryMatchHolder(query, obj);
      const filterMatches = activeFilterBlock
        ? activeFilterBlock.Matches(obj, GetDoc(), false)
        : true;

      return queryMatches && filterMatches;
    });

    for (let holderObj of holdersObjectList ?? []) {
      holdersObjects.add(holderObj);
    }

    return Array.from(holdersObjects)
      .map((item: any) => item.JSON())
      .sort((a, b) => {
        if (a[sort.fieldName] < b[sort.fieldName]) return sort.order === "asc" ? -1 : 1;
        if (a[sort.fieldName] > b[sort.fieldName]) return sort.order === "asc" ? 1 : -1;
        return 0;
      });
  }

  async handleAssignment(
    added: QObjDBObject[],
    removed: QObjDBObject[],
    dbObj: Nullable<QObjDBObject>
  ) {
    const wsMgr = GetWSMgr();
    wsMgr?.StartCheckPoint();

    if (dbObj) {
      await Promise.all(
        added.map(async (holderDataObj: QObjDBObject) => {
          await OverrideFactory.ReassignHolder(holderDataObj, dbObj);
        })
      );
    }
    await OverrideFactory.UnassignHolders(removed);

    wsMgr?.EndCheckPoint();

    wsMgr?.ReloadCurrentSubChart();
    wsMgr?.PrepRenderWS();
    wsMgr?.RenderWS();
  }

  protected queryMatchHolder(query: string, dbObj: QObjDBObject) {
    const lowerQuery = query.toLowerCase();
    const nameMatches = ((dbObj.SmartGetFieldValue(kNameID) as string) ?? "")
      .toLowerCase()
      .includes(lowerQuery);
    const titleMatches = ((dbObj.SmartGetFieldValue(kTitleID) as string) ?? "")
      .toLowerCase()
      .includes(lowerQuery);
    const employeeIDMatches = ((dbObj.SmartGetFieldValue(kEmployeeID) as string) ?? "")
      .toLowerCase()
      .includes(lowerQuery);

    return nameMatches || titleMatches || employeeIDMatches;
  }

  protected queryMatchPosition(query: string, dbObj: QObjDBObject) {
    const lowerQuery = query.toLowerCase();
    const titleMatches = ((dbObj.SmartGetFieldValue(kPositionTitleID) as string) ?? "")
      .toLowerCase()
      .includes(lowerQuery);
    const employeeIDMatches = ((dbObj.SmartGetFieldValue(kPositionID) as string) ?? "")
      .toLowerCase()
      .includes(lowerQuery);

    return titleMatches || employeeIDMatches;
  }
}
